// Landing page Whop redirects users to after a successful checkout.
// Polls /api/billing/me until the plan flips from free → paid (i.e.
// our webhook receiver + processor have caught up), then routes the
// user to /account/usage. Times out at 60s with a friendly fallback.

import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router";

import { GlassPanel, Pill, Surface } from "@/components/glass";
import { analytics } from "@/lib/analytics";

interface MyBilling {
	plan: { id: string };
	subscription: { status: string } | null;
}

const TIMEOUT_MS = 60_000;
const POLL_INTERVAL_MS = 2_000;

export default function BillingSuccess() {
	const navigate = useNavigate();
	const startedAt = useStartedAt();

	const meQ = useQuery({
		queryKey: ["billing-me-success"],
		queryFn: async (): Promise<MyBilling> => {
			const r = await fetch("/api/billing/me", { credentials: "include" });
			if (!r.ok) throw new Error(`me: ${r.status}`);
			return r.json();
		},
		refetchInterval: POLL_INTERVAL_MS,
	});

	const planId = meQ.data?.plan.id;
	const ready = planId === "pro";
	const elapsed = Date.now() - startedAt;
	const timedOut = elapsed > TIMEOUT_MS;

	const firedRef = useRef(false);
	useEffect(() => {
		if (!ready) return;
		// Spec 39: bottom of the payment funnel — the plan actually flipped.
		if (!firedRef.current && planId) {
			firedRef.current = true;
			analytics.capture("subscription_succeeded", { plan: planId });
		}
		// Small delay so the user sees the success state, then redirect.
		const t = setTimeout(() => navigate("/account/usage"), 1200);
		return () => clearTimeout(t);
	}, [ready, planId, navigate]);

	return (
		<Surface className="min-h-screen flex items-center justify-center p-4">
			<GlassPanel className="p-8 w-full max-w-md text-center space-y-6">
				{ready ? (
					<>
						<CheckCircle2 className="w-10 h-10 mx-auto text-[var(--color-success)]" />
						<div className="space-y-1">
							<div className="text-[16px] font-medium">You're on {planId}.</div>
							<div className="text-[12px] text-[var(--color-text-muted)]">
								Redirecting to your account…
							</div>
						</div>
					</>
				) : timedOut ? (
					<>
						<div className="space-y-1">
							<div className="text-[16px] font-medium">Almost there</div>
							<div className="text-[12px] text-[var(--color-text-muted)] max-w-[300px] mx-auto">
								Whop is still confirming the payment. Your plan will
								update automatically in a minute. You can close this
								tab — the next time you visit, the new plan will be live.
							</div>
						</div>
						<div className="flex justify-center gap-2">
							<Link to="/account/usage">
								<Pill>Go to account</Pill>
							</Link>
						</div>
					</>
				) : (
					<>
						<Loader2 className="w-10 h-10 mx-auto animate-spin text-[var(--color-text-muted)]" />
						<div className="space-y-1">
							<div className="text-[16px] font-medium">
								Thanks for subscribing
							</div>
							<div className="text-[12px] text-[var(--color-text-muted)]">
								Activating your plan…
							</div>
						</div>
					</>
				)}
			</GlassPanel>
		</Surface>
	);
}

function useStartedAt(): number {
	// Stable across renders so the timeout countdown doesn't drift.
	const ref = useRefStartedAt();
	return ref;
}

let cachedStartedAt: number | null = null;
function useRefStartedAt(): number {
	if (cachedStartedAt == null) cachedStartedAt = Date.now();
	useEffect(
		() => () => {
			cachedStartedAt = null;
		},
		[],
	);
	return cachedStartedAt;
}
